<template>
  <div id="admin">
    <header class="admin-header">
      <div class="left">
        <router-link tag="button" to="home" class="button">
          <span class="icon"><Icon :icon="mdiPencil"></Icon></span>
          <span>To Annotator</span>
        </router-link>
        <modal-button
          label="Switch Project"
          :icon="mdiFolderMultipleOutline"
          :modal-setting="{
            hasModalCard: true,
            trapFocus: true
          }"
        >
          <project-selector-admin :syncPage="syncPage" />
        </modal-button>
      </div>
      <div class="center">
        <div class="floorplan">
          <span>{{ selectedProjectId }}</span>
        </div>
      </div>
      <div class="right">
        <modal-button
          label="Create User"
          :icon="mdiAccount"
          :modal-setting="{
            hasModalCard: true,
            trapFocus: true
          }"
        >
          <create-user-form />
        </modal-button>
        <modal-button
          label="Modify Label Types"
          :icon="mdiTag"
          :modal-setting="{
            hasModalCard: true,
            trapFocus: true
          }"
        >
          <label-type-manager
        /></modal-button>
      </div>
    </header>
    <main>
      <div class="image-list-container">
        <header>
          <div class="left" style="width: 100%">
            <b-field
              label="Filter on"
              label-position="on-border"
              style="width: 100%"
            >
              <b-select v-model="filterOn">
                <option value="name">Name</option>
                <option value="assignedUser">Assigned To</option>
              </b-select>
              <b-select
                v-if="filterOn === 'assignedUser'"
                v-model="filterVal"
                expanded
              >
                <option value="" key="UNASSIGNED">UNASSIGNED</option>
                <option
                  v-for="username in users"
                  :value="username"
                  :key="username"
                  >{{ username }}</option
                >
              </b-select>
              <b-input v-else v-model="filterValName" expanded></b-input>
              <b-button @click="onClickApplyFilter">Filter</b-button>
            </b-field>
            <b-button @click="onClickResetFilter">Reset</b-button>
          </div>
        </header>
        <header>
          <div class="left">
            <section>
              <b-button
                type="is-info"
                :disabled="!canAssign"
                @click="isModalActive = true"
                >Assign Selected To User</b-button
              >
              <b-modal :active.sync="isModalActive" has-modal-card>
                <assigner :reloadTable="reloadTable" />
              </b-modal>
            </section>
            <section>
              <b-field label="Version" label-position="on-border">
                <b-select v-model="version">
                  <option value="00">00</option>
                  <option value="01">01</option>
                </b-select>
                <b-button type="is-info" @click="isBulkModalActive = true"
                  >Bulk Assign</b-button
                >
              </b-field>
              <b-modal :active.sync="isBulkModalActive" has-model-card>
                <bulk-assigner :reloadTable="reloadTable" :version="version" />
              </b-modal>
            </section>
          </div>
          <div class="right">
            <section>
              <b-button @click="isStatsModalActive = true">Stats</b-button>
              <b-modal :active.sync="isStatsModalActive" has-model-card>
                <stats-modal :filters="filters" />
              </b-modal>
            </section>
          </div>
        </header>
        <div class="image-list">
          <admin-floorplan-table
            :floorplans="project.floorplansAdmin"
            :syncPage="syncPage"
            :currentPage="currentTablePage"
            :total="total"
            :perPage="perPage"
          />
        </div>
      </div>
      <div class="image-viewer-container">
        <div
          v-if="Object.keys(selectedImageRow).length > 0"
          class="image-viewer-if"
        >
          <svg preserveAspectRatio="xMidYMid meet" :viewBox="viewBoxString">
            <image :href="imageUrl" x="0" y="0" />
          </svg>
        </div>
      </div>
    </main>
    <div class="main-container"></div>
  </div>
</template>

<script>
import isEmpty from "lodash/isEmpty";
import { SnackbarProgrammatic as Snackbar } from "buefy";

import ui from "@/store/modules/ui";
import AdminFloorplanTable from "@/components/AdminFloorplanTable";
import Assigner from "@/components/Assigner";
import ModalButton from "@/components/ModalButton";
import CreateUserForm from "@/components/CreateUserForm";
import LabelTypeManager from "@/components/LabelTypeManager";
import ProjectSelectorAdmin from "@/components/ProjectSelectorAdmin";
import BulkAssigner from "@/components/BulkAssigner";
import StatsModal from "@/components/StatsModal";
import { baseUrl } from "@/config";
import {
  mdiPencil,
  mdiAccount,
  mdiTag,
  mdiFolderMultipleOutline
} from "@mdi/js";
import Icon from "../components/Icon";
import $axios from "@/axios-instance";
import { ref, computed, reactive, watchEffect } from "@vue/composition-api";
import useUsers from "@/composables/useUsers";

export default {
  components: {
    AdminFloorplanTable,
    Assigner,
    BulkAssigner,
    StatsModal,
    ModalButton,
    CreateUserForm /* ProjectSelector*/,
    LabelTypeManager,
    Icon,
    ProjectSelectorAdmin
  },
  setup() {
    // Composables
    const { users } = useUsers();
    // Ui States
    const isModalActive = ref(false);
    const isBulkModalActive = ref(false);
    const isStatsModalActive = ref(false);
    const filterOn = ref("assignedUser");
    const filterVal = ref("");
    const filterValName = ref("");
    const filters = ref({});
    const version = ref("00");
    // Table related states
    const currentTablePage = ref(1);
    const total = ref(0);
    const perPage = ref(15);
    //
    // Image
    // ------------------------------------------------------------------------
    const imageUrl = computed(() => {
      const urlPrefix = baseUrl + "static/images/";
      // Sometimes the filename is not specified so substitute it by its name.
      // Works out most of the time.
      if (!ui.selectedImageRow.filename) {
        return urlPrefix + ui.selectedImageRow.name;
      }
      return urlPrefix + ui.selectedImageRow.filename;
    });
    const image = new Image();
    const imageSize = reactive({
      width: 0,
      height: 0
    });
    // Reload image everytime imageUrl changes
    watchEffect(() => {
      image.onload = () => {
        imageSize.width = image.width;
        imageSize.height = image.height;
      };
      image.src = imageUrl.value;
    });
    // Define viewbox for svg canvas based on image dimensions.
    const viewBoxString = computed(() => {
      const marginRatio = 0.2;
      const minX = -(imageSize.width * marginRatio);
      const minY = -(imageSize.height * marginRatio);
      const width = imageSize.width * (1 + marginRatio * 2);
      const height = imageSize.height * (1 + marginRatio * 2);
      return `${minX} ${minY} ${width} ${height}`;
    });

    const canAssign = computed(() => {
      return !isEmpty(ui.checkedImageRows);
    });
    const selectedProjectId = computed(() => {
      return ui.selectedProjectId;
    });
    // Reload Table
    const reloadTable = () => {
      syncPage(currentTablePage.value);
    };
    // Add new filter to list of applied filters.
    const applyFilter = () => {
      switch (filterOn.value) {
        case "assignedUser":
          filters.value["assignedUser"] = filterVal.value;
          break;
        case "name":
          filters.value["name"] = `.*${filterValName.value}.*`;
          break;
        default:
      }
    };
    // Reset filters
    const resetFilters = () => {
      filters.value = {};
    };
    // Event callback for Filter button.
    const onClickApplyFilter = () => {
      applyFilter();
      reloadTable();
    };
    // Event callback for Reset (filter) button.
    const onClickResetFilter = () => {
      resetFilters();
      reloadTable();
    };
    // Fetch new page from server using the current state.
    async function syncPage(page) {
      // Fetch selected project
      try {
        await ui.fetchCurrentUser();
      } catch (error) {
        console.error(`syncPage in Admin failed at fetchCurrentUser: ${error}`);
        Snackbar.open({
          message: error.message,
          actionText: "Close",
          type: "is-warning",
          indefinite: true,
          position: "is-top"
        });
      }
      // Throw if no project is selected
      if (!ui.selectedProjectId) {
        throw new Error("Project not selected");
      }
      // Setup query parameters
      //     Setup pagination
      const paramsArr = [`page=${page}`, `limit=${perPage.value}`];
      //     Apply filters
      Object.entries(filters.value).forEach(([on, val]) => {
        paramsArr.push(`${on}=${val}`);
      });
      //     Join parameters into a query string
      const params = paramsArr.join("&");
      // Call API
      const res = await $axios.get(
        `/projects/${ui.selectedProjectId}/floorplans?${params}`
      );
      const data = res.data;
      if (!data || !data.success) {
        throw new Error("Server Access Failed");
      }
      // Place fetched floorplan infos in global state
      // TODO: maybe its possible to keep it local ?
      ui.setProjectFloorplansAdmin(data.payload.docs);
      // Buefy Table requires these to make itself work
      total.value = data.payload.totalDocs;
      currentTablePage.value = data.payload.currentPage;
    }

    return {
      // Composables
      users,
      // Ui States
      isModalActive,
      isBulkModalActive,
      isStatsModalActive,
      filters,
      filterOn,
      filterVal,
      filterValName,
      canAssign,
      version,
      // Image related,
      viewBoxString,
      // Table related
      currentTablePage,
      total,
      perPage,
      // Computed
      imageUrl,
      selectedProjectId,
      project: computed(() => ui.project),
      selectedImageRow: computed(() => ui.selectedImageRow),
      // Methods
      syncPage,
      onClickApplyFilter,
      onClickResetFilter,
      reloadTable,
      // Icons
      mdiPencil,
      mdiAccount,
      mdiTag,
      mdiFolderMultipleOutline
    };
  }
};
</script>

<style lang="scss" scoped>
#admin {
  display: flex;
  flex-direction: column;
  height: 100%;
}

header {
  flex: 0 0 auto;
  padding: 0.5rem;
  display: flex;
  justify-content: space-between;
}

header > .left {
  display: flex;
  & > * {
    margin-right: 0.5rem;
  }
}
header > .center {
  flex: 1 0 0;
}
header > .right {
  display: flex;
  flex-direction: row-reverse;
  & > * {
    margin-left: 0.5rem;
  }
}

.spacer {
  flex: 1 1 0;
}

.admin-header {
  // border-bottom: solid 2px $border-grey;
  box-shadow: 0 0 10px $border-grey;
}

main {
  display: flex;
  flex-direction: row;
  height: 100%;
}

.image-list-container {
  flex: 1 0 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  // border-right: solid 2px $border-grey;
}

.image-list {
  overflow-y: scroll;
  overflow-x: hidden;
  flex: 1 1 0;
}

.image-viewer-container {
  flex: 1 1 0;
  height: 100%;
}

.image-viewer-if {
  height: 100%;
}

.floorplan {
  margin-left: 1em;
  padding-top: calc(0.4em - 1px);
  padding-bottom: calc(0.4em - 1px);
  height: 2.5em;
  vertical-align: middle;
  text-align: center;
  & > span {
    font-size: 1.2rem;
    line-height: 1.5;
  }
}

svg {
  height: 100%;
  width: 100%;
}

.image-list {
  overflow-y: auto;
  height: 100%;
}
</style>
